/**
 * Channel-row helpers for adding every channel of a multi-channel device in
 * one go (pour create wizard + pour edit page).
 *
 * The user types a location name per channel ("Pier 3 Top", ...). Each
 * ticked channel saves as its name. Channels already on the pour show greyed
 * with their existing name. Everything here is pure so both dialogs, the
 * hook and the tests share one set of rules.
 *
 * Temperature differentials between channels of the same device are kept
 * per pair: each ticked channel's checklist lists the other ticked
 * channels, and a pair is the same from either side (ChannelDiffPair,
 * toggleChannelDiffPair, channelDiffPairs). Pairs with locations already on
 * the pour are kept per channel by the hook.
 */

import type { LocationFunctionValue } from './locationFunction';

/** Channel numbers a ConXagent exposes. */
export const CHANNEL_NUMBERS: number[] = [1, 2, 3, 4];

/** Legacy PourLocation.LocationID column width. */
export const LOCATION_NAME_MAX = 50;

/** Hardware types that add all their channels at once. ConXagent only:
 *  ConXsensors stay single-channel, TMC is one tank per location, ConXenviro
 *  already expands automatically and a Weather Station has no channels. */
export const isMultiChannelHardware = (hw?: string | null): boolean => hw === 'ConXagent';

/** Collapse runs of whitespace and trim, as the legacy app saved names. */
export const normalizeLocationName = (name?: string | null): string =>
  (name || '').replace(/\s+/g, ' ').trim();

/** Comparison key for location names: normalized and lower-cased, so
 *  "Pier 3  Top" and "pier 3 top" count as the same name. */
export const locationNameKey = (name?: string | null): string =>
  normalizeLocationName(name).toLowerCase();

/** A location already on the pour (saved, or a draft row in the wizard),
 *  reduced to what the channel rules need. */
export interface PourChannelRef {
  loggerId: string;
  channelNo: number | string | null | undefined;
  name: string | null | undefined;
}

/** Channels of one logger already used on the pour, as channel number to
 *  the existing location's name (normalized, may be ''). */
export const usedChannelsOnLogger = (
  refs: ReadonlyArray<PourChannelRef>,
  loggerId: string,
): Map<number, string> => {
  const used = new Map<number, string>();
  if (!loggerId) return used;
  refs.forEach((r) => {
    if (r.loggerId !== loggerId) return;
    if (r.channelNo == null || r.channelNo === '') return;
    const no = Number(r.channelNo);
    if (!Number.isInteger(no) || no < 1) return;
    // First location wins when legacy data holds two on one channel.
    if (!used.has(no)) used.set(no, normalizeLocationName(r.name));
  });
  return used;
};

/** True when every channel in `nos` of the logger is already on the pour,
 *  so the device has nothing left to add. */
export const isLoggerFullyUsed = (
  refs: ReadonlyArray<PourChannelRef>,
  loggerId: string,
  nos: ReadonlyArray<number> = CHANNEL_NUMBERS,
): boolean => {
  if (!loggerId || nos.length === 0) return false;
  const used = usedChannelsOnLogger(refs, loggerId);
  return nos.every((no) => used.has(no));
};

/** True when `name` matches one of `existingNames` (case and spacing
 *  ignored). A blank name never counts as a duplicate. Callers leave the
 *  location being edited out of `existingNames`. */
export const isDuplicateLocationName = (
  name: string | null | undefined,
  existingNames: ReadonlyArray<string | null | undefined>,
): boolean => {
  const key = locationNameKey(name);
  if (!key) return false;
  return existingNames.some((n) => locationNameKey(n) === key);
};

/** Why a ticked channel row can't be saved yet. */
export type ChannelRowError = 'blank' | 'tooLong' | 'duplicateOnPour' | 'duplicateInBatch';

/** Plain wording for each row error, shown under the name field and in the
 *  dialog's error list ("Channel 2: Enter a name."). */
export const CHANNEL_ROW_ERROR_TEXT: Record<ChannelRowError, string> = {
  blank: 'Enter a name.',
  tooLong: `Name is too long (${LOCATION_NAME_MAX} characters or fewer).`,
  duplicateOnPour: 'A location with this name is already on this pour.',
  duplicateInBatch: 'Another ticked channel has the same name.',
};

export interface ChannelRow {
  no: number;
  /** Channel already has a location on this pour. Used rows are never
   *  checked and carry no error. */
  used: boolean;
  /** Existing location's name for a used channel ('' when unknown). */
  usedByName: string;
  checked: boolean;
  /** The location name as typed. */
  name: string;
  /** What the location saves as: the name, normalized. */
  finalName: string;
  /** Only checked rows carry errors. */
  error: ChannelRowError | null;
  /** The channel's Function (Temperature Only, ambient or Concrete
   *  Maturity). Never 'maturity' on a pour without maturity. */
  fn: LocationFunctionValue;
  /** fn is Concrete Maturity. */
  maturity: boolean;
}

export interface BuildChannelRowsArgs {
  channelNos: ReadonlyArray<number>;
  used: Map<number, string>;
  channelNames: Record<number, string>;
  /** Free channels the user unticked. Every other free channel is ticked. */
  unticked: ReadonlyArray<number>;
  /** Names of every location already on the pour. */
  existingNames: ReadonlyArray<string | null | undefined>;
  /** The pour lets concrete locations compute maturity. Channels then
   *  default to Concrete Maturity, else to Temperature Only. */
  maturityAllowed?: boolean;
  /** Functions the user picked, by channel number; the rest default. */
  functions?: Readonly<Record<number, LocationFunctionValue>>;
}

/** One row per channel with its tick, final name and first error. Error
 *  order: blank, then too long, then already on the pour, then clashing
 *  with another ticked row. */
export const buildChannelRows = ({
  channelNos,
  used,
  channelNames,
  unticked,
  existingNames,
  maturityAllowed = false,
  functions = {},
}: BuildChannelRowsArgs): ChannelRow[] => {
  const existingKeys = new Set<string>();
  existingNames.forEach((n) => {
    const key = locationNameKey(n);
    if (key) existingKeys.add(key);
  });

  const base = channelNos.map((no) => {
    const isUsed = used.has(no);
    const name = channelNames[no] ?? '';
    return {
      no,
      used: isUsed,
      usedByName: isUsed ? used.get(no) || '' : '',
      checked: !isUsed && !unticked.includes(no),
      name,
      finalName: normalizeLocationName(name),
      fn: channelFunction(functions[no], maturityAllowed),
    };
  });

  // How many ticked, named rows share each final name.
  const batchCounts: Record<string, number> = {};
  base.forEach((r) => {
    if (!r.checked || !normalizeLocationName(r.name)) return;
    const key = locationNameKey(r.finalName);
    batchCounts[key] = (batchCounts[key] || 0) + 1;
  });

  return base.map((r) => {
    let error: ChannelRowError | null = null;
    if (r.checked) {
      const key = locationNameKey(r.finalName);
      if (!normalizeLocationName(r.name)) error = 'blank';
      else if (r.finalName.length > LOCATION_NAME_MAX) error = 'tooLong';
      else if (existingKeys.has(key)) error = 'duplicateOnPour';
      else if ((batchCounts[key] || 0) > 1) error = 'duplicateInBatch';
    }
    return { ...r, error, maturity: r.fn === 'maturity' };
  });
};

/** A channel's Function: the one picked, else the pour's default. A pour
 *  without maturity turns a remembered Concrete Maturity into Temperature
 *  Only. */
const channelFunction = (
  picked: LocationFunctionValue | undefined,
  maturityAllowed: boolean,
): LocationFunctionValue => {
  const fn = picked ?? (maturityAllowed ? 'maturity' : 'temp');
  return fn === 'maturity' && !maturityAllowed ? 'temp' : fn;
};

/** A channel as its differential checklist names it: "Ch 3 Bottom", or
 *  just "Ch 3" while the name is blank. */
export const channelShortLabel = (row: Pick<ChannelRow, 'no' | 'name'>): string => {
  const name = normalizeLocationName(row.name);
  return name ? `Ch ${row.no} ${name}` : `Ch ${row.no}`;
};

/** The channels a row can be paired with: every other ticked channel of the
 *  device (a ticked channel is never one already on the pour), in row order. */
export const channelDiffPartners = (rows: ReadonlyArray<ChannelRow>, no: number): ChannelRow[] =>
  rows.filter((r) => r.checked && r.no !== no);

/** Checklist id of another channel of the device being added ("ch:3"), so
 *  it can't clash with a pour location's key. */
export const siblingDiffId = (no: number): string => `ch:${no}`;

/** The channel number in a siblingDiffId, or null for a pour location key. */
export const siblingNoFromDiffId = (id: string): number | null => {
  const m = /^ch:(\d+)$/.exec(id);
  return m ? Number(m[1]) : null;
};

/** A temperature differential between two channels of the device being
 *  added, as channel numbers with the lower one first (a < b). Pairs have no
 *  direction: Top with Bottom is the same pair as Bottom with Top. */
export interface ChannelDiffPair {
  a: number;
  b: number;
}

/** True when the pair of channels `x` and `y` (either order) is in `pairs`. */
export const hasChannelDiffPair = (pairs: ReadonlyArray<ChannelDiffPair>, x: number, y: number): boolean => {
  const a = Math.min(x, y);
  const b = Math.max(x, y);
  return pairs.some((p) => p.a === a && p.b === b);
};

/** `pairs` with the pair of channels `x` and `y` (either order) removed when
 *  it is there and added when it is not, kept in channel order. A channel is
 *  never paired with itself. */
export const toggleChannelDiffPair = (
  pairs: ReadonlyArray<ChannelDiffPair>,
  x: number,
  y: number,
): ChannelDiffPair[] => {
  if (x === y) return pairs.slice();
  const a = Math.min(x, y);
  const b = Math.max(x, y);
  if (hasChannelDiffPair(pairs, a, b)) return pairs.filter((p) => !(p.a === a && p.b === b));
  return [...pairs, { a, b }].sort((p, q) => p.a - q.a || p.b - q.b);
};

/** The remembered pairs that apply to this device: both channels ticked
 *  (so neither is already on the pour). In channel order, each pair once. */
export const channelDiffPairs = (
  rows: ReadonlyArray<ChannelRow>,
  pairs: ReadonlyArray<ChannelDiffPair>,
): ChannelDiffPair[] => {
  const ticked = new Set(rows.filter((r) => r.checked).map((r) => r.no));
  const seen = new Set<string>();
  const effective: ChannelDiffPair[] = [];
  pairs.forEach((p) => {
    const a = Math.min(p.a, p.b);
    const b = Math.max(p.a, p.b);
    const key = `${a}-${b}`;
    if (a === b || seen.has(key) || !ticked.has(a) || !ticked.has(b)) return;
    seen.add(key);
    effective.push({ a, b });
  });
  return effective.sort((p, q) => p.a - q.a || p.b - q.b);
};

/** Messages that block saving the batch: nothing ticked, or one line per
 *  ticked row with an error. Empty when the batch can be saved. */
export const channelBatchErrors = (rows: ReadonlyArray<ChannelRow>): string[] => {
  const checked = rows.filter((r) => r.checked);
  if (checked.length === 0) {
    if (rows.length > 0 && rows.every((r) => r.used)) {
      return ['Every channel on this device is already on this pour.'];
    }
    return ['Tick at least one channel.'];
  }
  return checked
    .filter((r) => r.error)
    .map((r) => `Channel ${r.no}: ${CHANNEL_ROW_ERROR_TEXT[r.error as ChannelRowError]}`);
};
